import React, { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Loader2, Plus, Shield, Pencil, KeyRound, Ban, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { adminApi, apiErr } from "@/lib/api";

const ROLE = { super_admin: "Super Admin", billing_admin: "Billing Admin", support_admin: "Support Admin", technical_admin: "Technical Admin" };

export default function AdminSecurity() {
  return (
    <div className="space-y-6">
      <div><h1 className="text-2xl font-bold font-display">Security & Access</h1><p className="text-sm text-slate-500">Admin team, client users and the audit trail.</p></div>
      <Tabs defaultValue="admins">
        <TabsList><TabsTrigger value="admins" data-testid="sec-tab-admins">Admin Users</TabsTrigger><TabsTrigger value="clients" data-testid="sec-tab-clients">Client Users</TabsTrigger><TabsTrigger value="audit" data-testid="sec-tab-audit">Audit Log</TabsTrigger></TabsList>
        <TabsContent value="admins"><AdminUsers /></TabsContent>
        <TabsContent value="clients"><ClientUsers /></TabsContent>
        <TabsContent value="audit"><AuditLog /></TabsContent>
      </Tabs>
    </div>
  );
}

function AdminUsers() {
  const [users, setUsers] = useState(null);
  const [show, setShow] = useState(false);
  const [edit, setEdit] = useState(null);
  const load = () => adminApi.get("/admin/security/admin-users").then((r) => setUsers(r.data)).catch(() => setUsers([]));
  useEffect(() => { load(); }, []);
  return (
    <div className="mt-4 space-y-3">
      <div className="flex justify-end"><Button style={{ background: "#7C3AED" }} onClick={() => setShow(true)} data-testid="admin-user-add"><Plus className="w-4 h-4 mr-1" />Add Admin</Button></div>
      <Card className="border shadow-none overflow-x-auto">
        {!users ? <Loader /> :
          <table className="w-full text-sm"><thead><tr className="text-left text-xs text-slate-400 border-b"><th className="px-4 py-2">Name</th><th className="px-4 py-2">Email</th><th className="px-4 py-2">Role</th><th className="px-4 py-2">2FA</th><th className="px-4 py-2">Last Login</th><th className="px-4 py-2 text-right">Actions</th></tr></thead>
            <tbody>{users.map((u) => <tr key={u.id} className="border-b" data-testid={`admin-user-${u.id}`}><td className="px-4 py-3 font-medium flex items-center gap-2"><Shield className="w-3.5 h-3.5" style={{ color: "#7C3AED" }} />{u.name}</td><td className="px-4 py-3">{u.email}</td><td className="px-4 py-3">{ROLE[u.role] || u.role}</td><td className="px-4 py-3">{u.two_fa_enabled ? "On" : "Off"}</td><td className="px-4 py-3 text-slate-400">{u.last_login ? u.last_login.slice(0, 16).replace("T", " ") : "—"}</td><td className="px-4 py-3 text-right"><Button size="sm" variant="outline" onClick={() => setEdit(u)} data-testid={`admin-user-edit-${u.id}`}><Pencil className="w-3.5 h-3.5 mr-1" />Edit</Button></td></tr>)}</tbody></table>}
      </Card>
      <AddAdmin open={show} onClose={() => setShow(false)} onDone={() => { setShow(false); load(); }} />
      <EditAdmin user={edit} onClose={() => setEdit(null)} onDone={() => { setEdit(null); load(); }} />
    </div>
  );
}

function EditAdmin({ user, onClose, onDone }) {
  const [f, setF] = useState({ name: "", role: "support_admin", two_fa_enabled: false, password: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (user) setF({ name: user.name || "", role: user.role || "support_admin", two_fa_enabled: !!user.two_fa_enabled, password: "" }); }, [user]);
  const submit = async () => {
    setBusy(true);
    try {
      const body = { name: f.name, role: f.role, two_fa_enabled: f.two_fa_enabled };
      if (f.password) body.password = f.password;
      await adminApi.patch(`/admin/security/admin-users/${user.id}`, body);
      toast.success(f.password ? "Admin updated & password reset" : "Admin updated"); onDone();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!user} onOpenChange={onClose}><DialogContent data-testid="edit-admin-dialog">
      <DialogHeader><DialogTitle>Edit Admin — {user?.email}</DialogTitle></DialogHeader>
      <div className="space-y-3">
        <div><Label>Name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="edit-admin-name" /></div>
        <div><Label>Role</Label><Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}><SelectTrigger data-testid="edit-admin-role"><SelectValue /></SelectTrigger><SelectContent>{Object.entries(ROLE).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select></div>
        <div className="flex items-center justify-between rounded-lg border p-3"><Label className="mb-0">Two-factor authentication (2FA)</Label><Switch checked={f.two_fa_enabled} onCheckedChange={(v) => setF({ ...f, two_fa_enabled: v })} data-testid="edit-admin-2fa" /></div>
        <div><Label>Reset password (optional)</Label><Input type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} placeholder="Leave blank to keep current password" data-testid="edit-admin-password" /></div>
      </div>
      <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button style={{ background: "#7C3AED" }} onClick={submit} disabled={busy} data-testid="edit-admin-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save changes"}</Button></DialogFooter>
    </DialogContent></Dialog>
  );
}

function AddAdmin({ open, onClose, onDone }) {
  const [f, setF] = useState({ name: "", email: "", password: "", role: "support_admin", two_fa_enabled: false });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.email || !f.password) { toast.error("Email and password required"); return; }
    setBusy(true);
    try { await adminApi.post("/admin/security/admin-users", f); toast.success("Admin added"); onDone(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}><DialogContent data-testid="add-admin-dialog">
      <DialogHeader><DialogTitle>Add Admin User</DialogTitle></DialogHeader>
      <div className="space-y-3">
        <div><Label>Name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
        <div><Label>Email</Label><Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="admin-user-email" /></div>
        <div><Label>Password</Label><Input type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="admin-user-password" /></div>
        <div><Label>Role</Label><Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{Object.entries(ROLE).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select></div>
      </div>
      <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button style={{ background: "#7C3AED" }} onClick={submit} disabled={busy} data-testid="admin-user-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add"}</Button></DialogFooter>
    </DialogContent></Dialog>
  );
}

function ClientUsers() {
  const [users, setUsers] = useState(null);
  const [reset, setReset] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const load = () => adminApi.get("/admin/security/client-users").then((r) => setUsers(r.data)).catch(() => setUsers([]));
  useEffect(() => { load(); }, []);
  const toggleStatus = async (u) => {
    if (!u.tenant_id) { toast.error("No client account linked to this user"); return; }
    const suspend = u.client_status === "active";
    setBusyId(u.id);
    try {
      await adminApi.post(`/admin/clients/${u.tenant_id}/${suspend ? "suspend" : "activate"}`);
      toast.success(suspend ? "Client access suspended" : "Client access activated"); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusyId(null); }
  };
  if (!users) return <Loader />;
  return (
    <>
    <Card className="border shadow-none mt-4 overflow-x-auto">
      <table className="w-full text-sm"><thead><tr className="text-left text-xs text-slate-400 border-b"><th className="px-4 py-2">Name</th><th className="px-4 py-2">Email</th><th className="px-4 py-2">Company</th><th className="px-4 py-2">Role</th><th className="px-4 py-2">Account</th><th className="px-4 py-2 text-right">Actions</th></tr></thead>
        <tbody>{users.map((u) => <tr key={u.id} className="border-b" data-testid={`client-user-${u.id}`}><td className="px-4 py-3 font-medium">{u.name}</td><td className="px-4 py-3">{u.email}</td><td className="px-4 py-3">{u.company_name || "—"}</td><td className="px-4 py-3 capitalize">{u.role}</td><td className="px-4 py-3"><span className={`capitalize text-xs px-2 py-0.5 rounded-full ${u.client_status === "active" ? "bg-emerald-100 text-emerald-700" : u.client_status === "suspended" ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-600"}`}>{u.client_status || "—"}</span></td><td className="px-4 py-3 text-right whitespace-nowrap">
          <Button size="sm" variant="outline" className="mr-2" onClick={() => setReset(u)} data-testid={`client-user-reset-${u.id}`}><KeyRound className="w-3.5 h-3.5 mr-1" />Reset password</Button>
          <Button size="sm" variant="outline" disabled={busyId === u.id} onClick={() => toggleStatus(u)} data-testid={`client-user-status-${u.id}`}>{busyId === u.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : u.client_status === "active" ? <><Ban className="w-3.5 h-3.5 mr-1 text-red-500" />Suspend</> : <><CheckCircle2 className="w-3.5 h-3.5 mr-1 text-emerald-500" />Activate</>}</Button>
        </td></tr>)}</tbody></table>
    </Card>
    <ResetClientPassword user={reset} onClose={() => setReset(null)} />
    </>
  );
}

function ResetClientPassword({ user, onClose }) {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setPw(""); }, [user]);
  const submit = async () => {
    if (pw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setBusy(true);
    try { await adminApi.post(`/admin/security/client-users/${user.id}/reset-password`, { password: pw }); toast.success(`Password reset for ${user.email}`); onClose(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!user} onOpenChange={onClose}><DialogContent data-testid="reset-client-dialog">
      <DialogHeader><DialogTitle>Reset password — {user?.email}</DialogTitle></DialogHeader>
      <div className="space-y-3">
        <div><Label>New password</Label><Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="At least 6 characters" data-testid="reset-client-password" /></div>
        <p className="text-xs text-slate-400">The client will need to use this new password to log in. Share it with them securely.</p>
      </div>
      <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button style={{ background: "#7C3AED" }} onClick={submit} disabled={busy} data-testid="reset-client-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Reset password"}</Button></DialogFooter>
    </DialogContent></Dialog>
  );
}

function AuditLog() {
  const [logs, setLogs] = useState(null);
  useEffect(() => { adminApi.get("/admin/audit-log").then((r) => setLogs(r.data)).catch(() => setLogs([])); }, []);
  if (!logs) return <Loader />;
  return (
    <Card className="border shadow-none mt-4 overflow-x-auto">
      {logs.length === 0 ? <div className="p-10 text-center text-sm text-slate-400">No actions logged yet.</div> :
        <table className="w-full text-sm"><thead><tr className="text-left text-xs text-slate-400 border-b"><th className="px-4 py-2">Admin</th><th className="px-4 py-2">Action</th><th className="px-4 py-2">Resource</th><th className="px-4 py-2">IP</th><th className="px-4 py-2">When</th></tr></thead>
          <tbody>{logs.map((l) => <tr key={l.id} className="border-b" data-testid="admin-audit-row"><td className="px-4 py-3">{l.admin_email}</td><td className="px-4 py-3 capitalize">{(l.action || "").replace(/_/g, " ")}</td><td className="px-4 py-3 text-slate-500">{l.resource_type}</td><td className="px-4 py-3 text-slate-400">{l.ip_address || "—"}</td><td className="px-4 py-3 text-slate-400">{(l.created_at || "").slice(0, 16).replace("T", " ")}</td></tr>)}</tbody></table>}
    </Card>
  );
}

function Loader() { return <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" style={{ color: "#7C3AED" }} /></div>; }
